// Netzfehler von „nicht angemeldet" trennen.
//
// `supabase.auth.getUser()` fragt den Auth-Server. Ohne Netz kommt KEIN
// Nutzer zurück, dazu ein `AuthRetryableFetchError` (Status 0) — das ist
// keine Abmeldung. Die Speicherwege warfen daraus bisher „Nicht eingeloggt",
// die Warteschlange stufte das als 'auth-required' ein, und auf der Baustelle
// stand rot „Anmeldung nötig", obwohl nur das Netz fehlte.
//
// Rein und ohne Supabase-Abhängigkeit (testbar ohne jsdom); den Online-Stand
// des Geräts reicht der Aufrufer herein (`geraetOnline`).

/** `name` des Fehlers, den ein Speicherweg ohne Netz (oder bei gestörtem Auth-Server) wirft. */
export const NETZ_FEHLER_NAME = 'NetzFehler';

/**
 * Der Text für den Bildschirm. Bewusst ohne „wird nachgeholt": derselbe
 * Wächter steht auch vor Wegen, die nichts nachholen (Projekt anlegen,
 * Import, Übernahme beantworten) — dort wäre das ein falsches Versprechen.
 * Dass die Warteschlange nachholt, zeigt sie über ihren Status ('offline').
 */
export const NETZ_FEHLER_TEXT = 'Keine Netzverbindung – bitte später erneut versuchen.';

/**
 * Derselbe Fehler, wenn nicht das Netz fehlt, sondern der Auth-Server gestört
 * ist (`istServerStoerung`). Das Netz des Geräts ist dann in Ordnung.
 */
export const SERVER_STOERUNG_TEXT = 'Server nicht erreichbar – bitte später erneut versuchen.';

/** fetch scheitert je nach Browser mit anderem Wortlaut — alle meinen „kein Netz" (wie foreignAccess.ts). */
const OFFLINE_MUSTER = /failed to fetch|networkerror|load failed|fetch failed|network request failed/i;

const feld = (fehler: unknown, schluessel: string): unknown =>
    fehler !== null && typeof fehler === 'object'
        ? (fehler as Record<string, unknown>)[schluessel]
        : undefined;

/**
 * Ist das ein Netzfehler — und keine Absage der Anmeldung?
 *
 * Ja bei:
 *   • dem eigenen NetzFehler und dem `AuthRetryableFetchError` von
 *     supabase-js (fetch gescheitert: Status 0; Gateway: 502/503/504);
 *   • Status 0;
 *   • einem Fehler OHNE Status, dessen Text nach gescheitertem fetch klingt
 *     („Failed to fetch", Safari „Load failed" …);
 *   • `online === false` — das Gerät meldet selbst, dass es offline ist.
 *
 * Nie bei einem 4xx: dann hat der Server abgesagt, oder supabase-js sagt
 * ausdrücklich, dass keine Sitzung da ist (`AuthSessionMissingError`, 400).
 * Das ist die echte Abmeldung, mit oder ohne Netz.
 *
 * Ohne `online` zählt nur der Fehler selbst — so stuft `classifySyncError`
 * ein, unabhängig davon, was das Gerät gerade meldet.
 */
export const istNetzFehler = (fehler: unknown, online?: boolean): boolean => {
    const name = feld(fehler, 'name');
    if (name === NETZ_FEHLER_NAME || name === 'AuthRetryableFetchError') return true;
    const status = feld(fehler, 'status');
    if (typeof status === 'number') {
        if (status >= 400 && status < 500) return false;
        if (status === 0) return true;
    } else {
        const text = feld(fehler, 'message');
        if (typeof text === 'string' && OFFLINE_MUSTER.test(text)) return true;
    }
    return online === false;
};

/**
 * Hat der Auth-Server versagt, statt abzusagen?
 *
 * Ja bei 5xx (`AuthApiError` 500, `AuthRetryableFetchError` 502/503/504) und
 * bei `AuthUnknownError`, einer Fehlerantwort ohne JSON. Die stammt von Proxy,
 * CDN oder der Anmeldeseite eines WLANs — der Auth-Server selbst antwortet
 * immer mit JSON. Abgesagt hat er nur mit einem 4xx oder
 * `AuthSessionMissingError`.
 */
const istServerStoerung = (fehler: unknown): boolean => {
    if (feld(fehler, 'name') === 'AuthUnknownError') return true;
    const status = feld(fehler, 'status');
    return typeof status === 'number' && status >= 500;
};

/**
 * Der Fehler, den ein Speicherweg ohne Netz wirft: Klartext in `message`
 * (bei gestörtem Auth-Server SERVER_STOERUNG_TEXT), Kennung in `name`, der
 * ursprüngliche Fehler in `cause` (Diagnose).
 */
export const netzFehler = (ursache?: unknown, text: string = NETZ_FEHLER_TEXT): Error => {
    const fehler = new Error(text);
    fehler.name = NETZ_FEHLER_NAME;
    if (ursache !== undefined && ursache !== null) fehler.cause = ursache;
    return fehler;
};

/**
 * Ist das der NetzFehler eines Speicherwegs — und nicht irgendein
 * gescheiterter fetch? Nur für ihn gelten der Takt ohne Wartezeit
 * (markSyncFailure) und die Anzeige 'offline' (useOfflineSync).
 */
export const istEigenerNetzFehler = (fehler: unknown): boolean =>
    feld(fehler, 'name') === NETZ_FEHLER_NAME;

/**
 * `getUser()` hat keinen Nutzer geliefert — was geworfen wird.
 *
 * Kein Netz oder gestörter Auth-Server → NetzFehler: die Warteschlange
 * wiederholt ihn ('retryable') ohne Wartezeit und zeigt 'offline'. Sonst
 * unverändert „Nicht eingeloggt" (→ 'auth-required').
 */
export const fehlerOhneNutzer = (anmeldeFehler: unknown, online: boolean | undefined): Error => {
    // Zuerst: kam eine Fehlerantwort an, liegt es nicht am Netz des Geräts.
    if (istServerStoerung(anmeldeFehler)) return netzFehler(anmeldeFehler, SERVER_STOERUNG_TEXT);
    if (istNetzFehler(anmeldeFehler, online)) return netzFehler(anmeldeFehler);
    return new Error('Nicht eingeloggt');
};

/** Was das Gerät über seine Verbindung meldet; `undefined`, wo es das nicht kann (Node, Tests). */
export const geraetOnline = (): boolean | undefined =>
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined;
